import {Component, OnDestroy, OnInit, signal} from '@angular/core';
import {AuthService} from "@auth0/auth0-angular";
import {ActivatedRoute, RouterLink} from "@angular/router";
import {Subscription} from "rxjs";
import {jwtDecode} from "jwt-decode";
import {NgIf} from "@angular/common";
import {LoadingComponent} from "../../shared/loading/loading.component";
import {AppUtils} from "../../core/utils/app.utils";

type ErrorInfo = {
  img: string;
  title: string;
  heading: string;
  message: string;
  hideLogin?: boolean;
};

@Component({
  selector: 'app-error',
  standalone: true,
  imports: [
    RouterLink,
    NgIf,
    LoadingComponent
  ],
  templateUrl: './error.component.html',
  styleUrl: './error.component.less'
})
export class ErrorComponent implements OnInit, OnDestroy {

  code!: string | number;
  error!: ErrorInfo;
  loading = signal(true);
  authenticated = signal(false);
  backLink: string = '';
  private readonly subscription = new Subscription();

  private static readonly ALLOWED_ERROR_CODES: Record<string, ErrorInfo> = {
    401: {
      img: '401.png',
      title: '401 – Unauthorized',
      heading: 'You must log in to access this page.',
      message: 'Please try logging in. If you think this is an error, please contact our ',
    },
    403: {
      img: '403.png',
      title: '403 – Forbidden',
      heading: 'You do not have sufficient permissions to access this page.',
      message: 'If you think this is an error, please contact our ',
    },
    '403-activate': {
      img: '403.png',
      title: '403 - Forbidden',
      heading: 'You must activate your account using the link in the Vodori Welcome email in order to sign in for the first time.',
      message:
        'If you think this is an error, please contact our ',
      hideLogin: true
    },
    404: {
      img: '404.png',
      title: '404 – Not Found',
      heading: 'This content cannot be found.',
      message: 'The page you are looking for cannot be found. If you think this is an error, please contact our ',
    },
    500: {
      img: '500.png',
      title: '500 - Internal Server Error',
      heading: 'Sorry, but we could not complete your request.',
      message: 'If this problem persists, please report it to the ',
    },
  };

  constructor(private readonly route: ActivatedRoute, private readonly authService: AuthService) {}

  ngOnInit() {
    const incomingCode = this.route.snapshot.params['code'];
    this.code = ErrorComponent.ALLOWED_ERROR_CODES[incomingCode] ? incomingCode : 500;
    this.error = ErrorComponent.ALLOWED_ERROR_CODES[this.code];

    this.subscription.add(
        this.authService.getAccessTokenSilently().subscribe({
          next: (jwtToken) => {
            const decodedToken: any = jwtDecode(jwtToken);
            const defaultLink = decodedToken["https://vodori.com/def"];
            this.backLink = defaultLink ? `/${defaultLink}` : '/';
            this.authenticated.set(true);
            this.loading.set(false);
          }, error: (error) => {
            this.loading.set(false);

            if (error?.message !== "Login required") {
              console.error('LOGIN: 🚫 - Error retrieving access token', error);
            }
          }
        })
    );
  }

  logout() {
    AppUtils.clearCloudSessionState();
    this.authService.logout({
      logoutParams: {
        returnTo: window.location.origin
      },
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

}
